const LIGHT = {
    'color-background-primary': '#ffffff',
    'color-background-secondary': '#f5f5f4',
    'color-background-tertiary': '#eeeee8',
    'color-background-info': '#E6F2FF',
    'color-background-danger': '#fcebeb',
    'color-background-success': '#eaf3de',
    'color-background-warning': '#faeeda',
    'color-text-primary': '#1a1a1a',
    'color-text-secondary': '#5f5e5a',
    'color-text-tertiary': '#888780',
    'color-text-info': '#006CEC',
    'color-text-danger': '#a32d2d',
    'color-text-success': '#3b6d11',
    'color-text-warning': '#854f0b',
    'color-border-tertiary': 'rgba(0,0,0,0.15)',
    'color-border-secondary': 'rgba(0,0,0,0.3)',
    'color-border-primary': 'rgba(0,0,0,0.4)',
    'color-border-info': '#006CEC',
    'color-text-warning': '#EC8000',
    'color-background-warning': '#FFF4E6',
};

const DARK = {
    'color-background-primary': '#1a1a1a',
    'color-background-secondary': '#2a2a2a',
    'color-background-tertiary': '#0f0f0f',
    'color-background-info': '#0c2d4a',
    'color-background-danger': '#4a1c1c',
    'color-background-success': '#1a3a0a',
    'color-background-warning': '#3a2800',
    'color-text-primary': '#e5e5e5',
    'color-text-secondary': '#a0a0a0',
    'color-text-tertiary': '#707070',
    'color-text-info': '#4DA3FF',
    'color-text-danger': '#f09595',
    'color-text-success': '#97c459',
    'color-text-warning': '#ef9f27',
    'color-border-tertiary': 'rgba(255,255,255,0.15)',
    'color-border-secondary': 'rgba(255,255,255,0.3)',
    'color-border-primary': 'rgba(255,255,255,0.4)',
    'color-border-info': '#4DA3FF',
    'color-text-warning': '#FFAD4D',
    'color-background-warning': '#3a2800',
};

const COLOR_RAMPS = {
    blue:   { 50: '#E6F2FF', 400: '#3B82F6', 600: '#006CEC', 800: '#002C5C' },
    orange: { 50: '#FFF7ED', 400: '#F97316', 600: '#EA580C', 800: '#9A3412' },
    teal:   { 50: '#F0FDFA', 400: '#14B8A6', 600: '#0D9488', 800: '#115E59' },
    violet: { 50: '#EDE9FE', 400: '#8B5CF6', 600: '#7C3AED', 800: '#5B21B6' },
    gray:   { 50: '#F3F4F6', 400: '#9CA3AF', 600: '#6B7280', 800: '#374151' },
    green:  { 50: '#F0FDF4', 400: '#22C55E', 600: '#16A34A', 800: '#166534' },
    amber:  { 50: '#FFFBEB', 400: '#F59E0B', 600: '#D97706', 800: '#92400E' },
    red:    { 50: '#FEF2F2', 400: '#EF4444', 600: '#DC2626', 800: '#991B1B' },
    pink:   { 50: '#FDF2F8', 400: '#EC4899', 600: '#DB2777', 800: '#9D174D' },
};

const COLOR_RAMPS_DARK = {
    blue:   { 50: '#002C5C', 200: '#006CEC', 100: '#E6F2FF', 600: '#3B82F6' },
    orange: { 50: '#9A3412', 200: '#EA580C', 100: '#FFF7ED', 600: '#F97316' },
    teal:   { 50: '#115E59', 200: '#0D9488', 100: '#F0FDFA', 600: '#14B8A6' },
    violet: { 50: '#5B21B6', 200: '#7C3AED', 100: '#EDE9FE', 600: '#8B5CF6' },
    gray:   { 50: '#374151', 200: '#6B7280', 100: '#F3F4F6', 600: '#9CA3AF' },
    green:  { 50: '#166534', 200: '#16A34A', 100: '#F0FDF4', 600: '#22C55E' },
    amber:  { 50: '#92400E', 200: '#D97706', 100: '#FFFBEB', 600: '#F59E0B' },
    red:    { 50: '#991B1B', 200: '#DC2626', 100: '#FEF2F2', 600: '#EF4444' },
    pink:   { 50: '#9D174D', 200: '#DB2777', 100: '#FDF2F8', 600: '#EC4899' },
};

function buildCssVars(vars) {
    return Object.entries(vars).map(([k, v]) => `--${k}: ${v};`).join('\n    ');
}

function buildRampVars(ramps) {
    const lines = [];
    for (const [name, stops] of Object.entries(ramps)) {
        for (const [stop, color] of Object.entries(stops)) {
            lines.push(`--c-${name}-${stop}: ${color};`);
        }
    }
    return lines.join('\n    ');
}

export function getThemeVarsCSS(isDark) {
    const vars = isDark ? DARK : LIGHT;
    const ramps = isDark ? COLOR_RAMPS_DARK : COLOR_RAMPS;
    return `:root {
    ${buildCssVars(vars)}
    ${buildRampVars(ramps)}
    --font-sans: system-ui, -apple-system, sans-serif;
    --font-serif: Georgia, 'Times New Roman', serif;
    --font-mono: ui-monospace, monospace;
    --border-radius-md: 8px;
    --border-radius-lg: 12px;
    --border-radius-xl: 16px;
    --p: var(--color-text-primary);
    --s: var(--color-text-secondary);
    --t: var(--color-text-tertiary);
    --bg2: var(--color-background-secondary);
    --b: var(--color-border-tertiary);
}`;
}

function buildSvgRampRules(isDark) {
    const ramps = isDark ? COLOR_RAMPS_DARK : COLOR_RAMPS;
    let css = '';
    for (const [name, stops] of Object.entries(ramps)) {
        const fill = stops[50];
        const stroke = isDark ? stops[200] : stops[600];
        const titleColor = isDark ? stops[100] : stops[800];
        const subtitleColor = isDark ? stops[200] : stops[600];
        css += `
.c-${name} > rect, .c-${name} > circle, .c-${name} > ellipse, .c-${name} > path.fill,
.c-${name} > g > rect, .c-${name} > g > circle, .c-${name} > g > ellipse { fill: ${fill}; stroke: ${stroke}; stroke-width: 0.5px; }
.c-${name} > text, .c-${name} > g > text,
.c-${name} > text.t, .c-${name} > g > text.t,
.c-${name} > text.th, .c-${name} > g > text.th { fill: ${titleColor}; }
.c-${name} > text.ts, .c-${name} > g > text.ts { fill: ${subtitleColor}; }`;
    }
    return css;
}

export function getSvgThemeCSS(isDark) {
    const vars = isDark ? DARK : LIGHT;
    const ramps = isDark ? COLOR_RAMPS_DARK : COLOR_RAMPS;

    return `.visualize-svg-container {
    ${buildCssVars(vars)}
    ${buildRampVars(ramps)}
    --font-sans: system-ui, -apple-system, sans-serif;
    --font-serif: Georgia, 'Times New Roman', serif;
    --font-mono: ui-monospace, monospace;
    --p: var(--color-text-primary);
    --s: var(--color-text-secondary);
    --t: var(--color-text-tertiary);
    --bg2: var(--color-background-secondary);
    --b: var(--color-border-tertiary);
}
.visualize-svg-container { overflow: visible; }
.visualize-svg-container svg { width: 100%; height: auto; display: block; overflow: visible; }
.visualize-svg-container text { font-family: var(--font-sans); }
.visualize-svg-container .t { font-size: 14px; font-weight: 400; fill: ${vars['color-text-primary']}; }
.visualize-svg-container .ts { font-size: 12px; font-weight: 400; fill: ${vars['color-text-secondary']}; }
.visualize-svg-container .th { font-size: 14px; font-weight: 500; fill: ${vars['color-text-primary']}; }
.visualize-svg-container rect.box, .visualize-svg-container .box rect { fill: ${vars['color-background-secondary']}; stroke: ${vars['color-border-tertiary']}; stroke-width: 0.5px; }
.visualize-svg-container .box, .visualize-svg-container .node { cursor: pointer; }
.visualize-svg-container .box:hover rect, .visualize-svg-container .node:hover rect { filter: brightness(0.95); }
.visualize-svg-container .arr { stroke: ${vars['color-text-secondary']}; stroke-width: 2; fill: none; }
.visualize-svg-container .leader { stroke: ${vars['color-text-tertiary']}; stroke-width: 1; stroke-dasharray: 4 3; fill: none; }
${buildSvgRampRules(isDark)}`;
}

export function getHtmlBaseCSS() {
    return `* { margin: 0; padding: 0; box-sizing: border-box; }
body {
    font-family: var(--font-sans);
    font-size: 16px;
    line-height: 1.7;
    color: var(--color-text-primary);
    background: transparent;
}
input, select, textarea, button { font-family: inherit; font-size: inherit; }
input[type="range"] { height: 4px; appearance: none; background: var(--color-border-secondary); border-radius: 2px; outline: none; }
input[type="range"]::-webkit-slider-thumb { appearance: none; width: 18px; height: 18px; border-radius: 50%; background: var(--color-background-primary); border: 0.5px solid var(--color-border-secondary); cursor: pointer; }
button { background: transparent; border: 0.5px solid var(--color-border-secondary); border-radius: var(--border-radius-md); padding: 8px 16px; cursor: pointer; color: var(--color-text-primary); }
button:hover { background: var(--color-background-secondary); }
button:active { transform: scale(0.98); }`;
}
